import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";
import RsAdNewV2 from "components/buttons/Adnew-v2";
import SaveButton from "components/buttons/save";
import SafeField from "helpers/SafeField";
import {
  getActionPlanCategories,
  addActionPlanCategory,
  updateActionPlanCategory,
  deleteActionPlanCategory,
  getAllActionPlans,
} from "store/actions";
import "./actionPlans.css";

const ADMIN_AGENT_ID = 0;

const ActionPlanFolders = props => {
  document.title = "Action Plans | Realty Space";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { categories, allPlans, loading } = useSelector(state => ({
    categories: state.ActionPlanReducer.categories,
    allPlans: state.ActionPlanReducer.allPlans,
    loading: state.ActionPlanReducer.loading,
  }));

  const [isRight, setIsRight] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState({});
  const [deleteModal, setDeleteModal] = useState(false);

  useEffect(() => {
    dispatch(getActionPlanCategories(ADMIN_AGENT_ID));
    dispatch(getAllActionPlans(ADMIN_AGENT_ID));
  }, [dispatch]);

  const planCountByFolder = useMemo(() => {
    const counts = {};
    (allPlans || []).forEach(plan => {
      const catId = plan.actionPlanCategory ?? plan.ActionPlanCategory;
      if (catId === undefined || catId === null) return;
      counts[catId] = (counts[catId] || 0) + 1;
    });
    return counts;
  }, [allPlans]);

  const folderRows = useMemo(
    () =>
      (categories || []).map(folder => {
        const folderId = folder.id ?? folder.Id;
        return {
          ...folder,
          id: folderId,
          actionPlanCategoryTitle:
            folder.actionPlanCategoryTitle ?? folder.ActionPlanCategoryTitle,
          actionPlanCount:
            (folder.actionPlanCount ?? planCountByFolder[folderId]) || 0,
        };
      }),
    [categories, planCountByFolder]
  );

  const toggle = () => setIsRight(!isRight);

  const openAddFolder = e => {
    if (e?.preventDefault) e.preventDefault();
    setSelectedFolder({});
    setIsEdit(false);
    setIsRight(true);
  };

  const openEditFolder = folder => {
    setSelectedFolder({
      id: folder.id ?? folder.Id,
      actionPlanCategoryTitle:
        folder.actionPlanCategoryTitle ?? folder.ActionPlanCategoryTitle ?? "",
    });
    setIsEdit(true);
    setIsRight(true);
  };

  const handleFolderSubmit = values => {
    const onDone = ok => {
      if (ok) toggle();
    };
    if (isEdit) {
      dispatch(
        updateActionPlanCategory({ id: selectedFolder.id, ...values }, onDone)
      );
    } else {
      dispatch(addActionPlanCategory(values, onDone));
    }
  };

  const onClickDelete = folder => {
    setSelectedFolder({
      id: folder.id ?? folder.Id,
      actionPlanCategoryTitle:
        folder.actionPlanCategoryTitle ?? folder.ActionPlanCategoryTitle,
    });
    setDeleteModal(true);
  };

  const handleDeleteFolder = () => {
    if (selectedFolder?.id !== undefined) {
      dispatch(deleteActionPlanCategory(selectedFolder));
      setDeleteModal(false);
    }
  };

  return (
    <>
      <ToastContainer autoClose={2000} />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteFolder}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>
          <Row className="mb20">
            <Col xl="6">
              <Breadcrumbs
                title={props.t("Settings")}
                breadcrumbItem={props.t("Action plans")}
              />
            </Col>
          </Row>

          <Row>
            <Col xl="12">
              <div className="ap-page-header mb-3">
                <span className="ap-icon-badge">
                  <i className="bx bx-folder" />
                </span>
                <div className="ap-title-block">
                  <h4>
                    {props.t("Action Plans")}
                    <span className="ap-count-pill">
                      {folderRows.length} {props.t("folders")}
                    </span>
                  </h4>
                  <p>
                    {props.t(
                      "Organize your automated follow-up sequences into folders."
                    )}
                  </p>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xl="12">
              <Card className="ap-card mt-2">
                <CardBody>
                  <div className="ap-toolbar justify-content-end">
                    <RsAdNewV2
                      to="#"
                      onClick={openAddFolder}
                      className="text-purple border-right"
                      iconClass="bx bx-folder"
                    >
                      {props.t("Add new folder")}
                    </RsAdNewV2>
                  </div>

                  <div className="table-responsive ap-datatable-wrap">
                    <DataTable
                      className="table table-bordered data-table align-middle table-nowrap mb-0 ap-plain-table"
                      value={folderRows}
                      loading={loading}
                      paginator
                      rows={10}
                      rowsPerPageOptions={[10, 20, 30, 50]}
                      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                      emptyMessage={
                        <div className="ap-empty-state">
                          <i className="bx bx-folder-open" />
                          {props.t("No action plans found")}
                        </div>
                      }
                    >
                      <Column
                        field="actionPlanCategoryTitle"
                        header={props.t("Folder Name")}
                        sortable
                        body={rowData => (
                          <Link
                            to={`/action-plans/${rowData.id}`}
                            className="ap-folder-name"
                          >
                            <span className="ap-folder-icon">
                              <i className="bx bx-folder" />
                            </span>
                            {rowData.actionPlanCategoryTitle || "Untitled"}
                          </Link>
                        )}
                      />
                      <Column
                        field="actionPlanCount"
                        header={props.t("Total Action Plans")}
                        body={rowData => (
                          <span className="ap-badge ap-badge-client">
                            {rowData.actionPlanCount}
                          </span>
                        )}
                      />
                      <Column
                        header={props.t("Actions")}
                        body={rowData => (
                          <div className="ap-actions-cell flex-wrap">
                            <ActionEdit
                              to="#"
                              onClick={e => {
                                e.preventDefault();
                                openEditFolder(rowData);
                              }}
                            />
                            <ActionDelete
                              to="#"
                              onClick={e => {
                                e.preventDefault();
                                onClickDelete(rowData);
                              }}
                            />
                            <Link
                              to={`/action-plans/${rowData.id}?add=1`}
                              className="ap-mini-btn"
                            >
                              <i className="bx bx-plus" />
                              {props.t("Add plan")}
                            </Link>
                            <Link
                              to={`/action-plans/${rowData.id}`}
                              className="ap-mini-btn"
                            >
                              <i className="bx bx-show" />
                              {props.t("View plan")}
                            </Link>
                          </div>
                        )}
                      />
                    </DataTable>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Offcanvas isOpen={isRight} direction="end" toggle={toggle} style={{ width: 400 }}>
            <OffcanvasHeader toggle={toggle} className="ap-offcanvas-header">
              {isEdit ? props.t("Edit Folder") : props.t("Add Folder")}
            </OffcanvasHeader>
            <OffcanvasBody>
              <Formik
                enableReinitialize
                initialValues={{
                  actionPlanCategoryTitle:
                    selectedFolder?.actionPlanCategoryTitle || "",
                }}
                validationSchema={Yup.object({
                  actionPlanCategoryTitle: Yup.string().required(
                    "Folder name is required"
                  ),
                })}
                onSubmit={handleFolderSubmit}
              >
                {({ errors, touched, handleChange }) => (
                  <Form className="needs-validation">
                    <FormGroup className="mb-3">
                      <Label>{props.t("Folder Name")}</Label>
                      <SafeField
                        name="actionPlanCategoryTitle"
                        onChange={handleChange}
                        type="text"
                        placeholder={props.t("Enter folder name")}
                        className={
                          "form-control" +
                          (errors.actionPlanCategoryTitle &&
                          touched.actionPlanCategoryTitle
                            ? " is-invalid"
                            : "")
                        }
                      />
                      <ErrorMessage
                        name="actionPlanCategoryTitle"
                        component="div"
                        className="invalid-feedback"
                      />
                    </FormGroup>
                    <div className="ap-offcanvas-footer">
                      <SaveButton>{props.t("Save")}</SaveButton>
                    </div>
                  </Form>
                )}
              </Formik>
            </OffcanvasBody>
          </Offcanvas>
        </Container>
      </div>
    </>
  );
};

ActionPlanFolders.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(ActionPlanFolders);
